import {
  QuestionKey,
  QuestionConfig,
  ONBOARDING_QUESTIONS,
  AnswerValue,
} from './constants';

/**
 * Get a question configuration by its key
 *
 * @param questionKey The unique identifier of the question
 * @returns The question configuration, or undefined if not found
 *
 */
export const getQuestionByKey = (
  questionKey: QuestionKey,
): QuestionConfig | undefined => {
  return ONBOARDING_QUESTIONS.find(({ key }) => key === questionKey);
};

/**
 * Get the visible questions based on responses to conditional questions
 *
 * @param responses Map of question keys to their answered values
 * @returns Array of questions that should be visible based on the current answers
 *
 * @example
 * const responses = new Map([
 *   [QuestionKey.OtcDiagnosis, AnswerValue.Yes],
 * ]);
 * const visibleQuestions = getVisibleQuestions(responses);
 */
export const getVisibleQuestions = (
  responses: Map<QuestionKey, AnswerValue>,
): QuestionConfig[] => {
  return ONBOARDING_QUESTIONS.filter(({ conditionalRule }) => {
    // If question has no conditional rule, it's always visible
    if (!conditionalRule) {
      return true;
    }

    // If question has a conditional rule, check if the dependency is met
    const dependencyAnswer = responses.get(
      conditionalRule.dependsOnQuestionKey,
    );

    return dependencyAnswer === conditionalRule.dependsOnAnswerValue;
  });
};

/**
 * Get the number of expected responses based on current answers
 * This accounts for the conditional FAMILY_SIDE question
 *
 * @param responses Map of question keys to their answered values
 * @returns The total number of responses that should be collected
 */
export const getExpectedResponseCount = (
  responses: Map<QuestionKey, AnswerValue>,
): number => {
  return getVisibleQuestions(responses).length;
};

/**
 * Check if the conditional FAMILY_SIDE question should be visible
 *
 * @param otcDiagnosisAnswer The answer to the OTC_DIAGNOSIS question
 * @returns true if OTC_DIAGNOSIS is answered with YES, false otherwise
 */
export const isConditionalQuestionVisible = (
  otcDiagnosisAnswer?: AnswerValue,
): boolean => {
  return otcDiagnosisAnswer === AnswerValue.Yes;
};
